import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { View } from '@olxbr/design-system'
import { Button, LoadingButton } from '@olxbr/ds-core'
import MailIcon from '../../atoms/MailIcon'

import Form from '../../atoms/Form'
import LoadingBackdrop from '../../molecules/LoadingBackdrop'
import Title from '../../atoms/Title'
import { GenericErrorBox } from '../../molecules/GenericErrorBox'
import MailCheckInput from '../../organisms/MailCheckInput'
import Lurker from '../../../util/lurker'
import behaviors from '../../../util/behaviors'
import { isValidEmail } from '../../../util/validators'
import medias from '../../../util/medias'
import BaseForm from '../BaseForm'
import Subtitle from '../../atoms/Subtitle'
import { sendEmailChangeRequest, validateConfirmationToken } from '../../../api/emailChangeApiCall'
import EmailChangeActivationSentModal from '../EmailChangeActivationSentModal'
import emailAlreadyExists from '../RegisterTemplate/emailExistMsg'

const lurker = new Lurker('email_change_send_mail')

const CenterWrapper = styled(View)`
  display: flex;
  flex-direction: row;
  justify-content: center;
`

const PageTitle = styled(Title)`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 24px;
  line-height: 32px;
`

const Description = styled(View)`
  display: flex;
  flex-direction: column;
  padding-top: 16px;
`

const DescriptionWrapper = styled(Description)`
  margin-top: 16px;
  margin-bottom: 32px;
`

const PageForm = styled(Form)`
  margin-top: 12px;
`

const TitleStart = styled(View)`
  margin-bottom: 8px;
`

const MailCheckFormItemWrapper = styled(View)`
  margin-bottom: 32px;
  ${medias.medium`
    margin-bottom: 16px;
  `}
`

const ErrorMsgStartView = styled(View)`
  margin-top: 16px;
  margin-bottom: 8px;
`

const defaultEmailErrorMsg = 'Por favor, insira um endereço de e-mail válido.'

const errors = {
  showRateLimitError: false,
  showEmailInBlackListError: false,
  showEmailDomainInBlockListError: false,
  showGenericError: false,
  showGenericErrorEmail: false,
  showInvalidTokenError: false,
}

class EmailChangeSendMailTemplate extends BaseForm {
  constructor(props) {
    super(props)

    this.state = {
      ...errors,
      isSubmitting: false,
      showMailSentModal: false,
      loading: true,
      validators: {
        email: {
          action: isValidEmail,
          message: defaultEmailErrorMsg,
        },
      },
      fields: {
        email: {
          value: '',
          pristine: true,
          valid: false,
        },
      },
    }
  }

  componentDidMount() {
    lurker.pageLoadEvt()
    validateConfirmationToken(this.props.token)
      .then(isConfirmationTokenValid => {
        this.setState({
          showInvalidTokenError: !isConfirmationTokenValid,
        })
      })
      .catch(() => this.setState({ showInvalidTokenError: true }))
      .finally(() => this.setState({ loading: false }))
  }

  onEmailChange = event => {
    this.onFieldChange(event, 'email', isValidEmail, defaultEmailErrorMsg, true)
  }

  onEmailBlur = event => {
    this.onFieldChange(event, 'email', isValidEmail, defaultEmailErrorMsg, false)
  }

  handleSubmit = event => {
    event.preventDefault()
    lurker.clickEvt('submit')
    this.setState({ isSubmitting: true })
    this.checkFormIsValid()
      .then(this.doSendEmail)
      .catch(() => {
        lurker.viewMessageEvt('error', {
          accounts_reason: 'invalid_fields',
          ...this.getInvalidFields(),
        })
      })
      .finally(() => this.setState({ isSubmitting: false }))
  }

  doSendEmail = () => {
    const { email } = this.state.fields
    const { token } = this.props
    sendEmailChangeRequest(email.value, token)
      .then(this.onEmailSent)
      .catch(e => this.onApiCallError(e))
      .finally(() => this.setState({ isSubmitting: false }))
  }

  renderErrorView = onRetryConfirmation => (
    <div className="flex flex-col items-center">
      <TitleStart>
        <PageTitle>Não foi possível confirmar seu e-mail</PageTitle>
      </TitleStart>
      <Description fontWeight={400}>
        <Subtitle>
          Para segurança da sua conta, a confirmação enviada por e-mail possui validade.
        </Subtitle>
      </Description>
      <DescriptionWrapper fontWeight={400}>
        <Subtitle>Por favor, tente novamente!</Subtitle>
      </DescriptionWrapper>
      <Button className="w-full" size="medium" onClick={onRetryConfirmation}>
        Tentar novamente
      </Button>
    </div>
  )

  onEmailSent = () => {
    const { email } = this.state.fields
    lurker.viewMessageEvt('success')
    this.setState({ showMailSentModal: true })
    if (this.props.onEmailSent) {
      this.props.onEmailSent(email.value)
    }
  }

  changeMailCancel = onClose => () => {
    lurker.clickEvt('cancel_link')
    if (onClose && typeof onClose === 'function') {
      onClose()
    }
  }

  showError = (error, extra) => this.setState({ ...errors, ...extra, [error]: true })

  showEmailAlreadyExistsError = () => {
    const { fields } = this.state
    fields.email.msg = emailAlreadyExists(this.props.onForgotPassword)
    fields.email.valid = false
    this.setState({ fields })
  }

  onApiCallError = (error = {}) => {
    if (this.props.onError) {
      this.props.onError()
    }
    const { response = {} } = error
    if (response.status === 401) {
      lurker.viewMessageEvt('error', {
        accounts_reason: 'unauthorized',
        accounts_status_code: response.status,
      })
      this.showError('showGenericError')
    } else if (response.status === 403) {
      if (response.data.message === 'EMAIL_DOMAIN_IN_BLOCKLIST') {
        lurker.viewMessageEvt('error', {
          accounts_reason: 'email_domain_in_blocklist',
          accounts_status_code: response.status,
        })
        this.showError('showEmailDomainInBlockListError')
      } else {
        lurker.viewMessageEvt('error', {
          accounts_reason: 'email_in_blacklist',
          accounts_status_code: response.status,
        })
        this.showError('showEmailInBlackListError')
      }
    } else if (response.status === 429) {
      lurker.viewMessageEvt('error', {
        accounts_reason: 'rate_limit_exceeded',
        accounts_status_code: response.status,
      })
      this.showError('showRateLimitError')
    } else if (response.status === 409) {
      lurker.viewMessageEvt('error', {
        accounts_reason: 'email_already_exists',
        accounts_status_code: response.status,
      })
      this.showEmailAlreadyExistsError()
    } else if (response.status === 404 && response.data.message === 'INVALID_TOKEN') {
      lurker.viewMessageEvt('error', {
        accounts_reason: 'invalid_token',
        accounts_status_code: response.status,
      })
      this.showError('showInvalidTokenError')
    } else if (response.status) {
      lurker.viewMessageEvt('error', {
        accounts_reason: 'unexpected_status_code',
        accounts_status_code: response.status,
      })
      this.showError('showGenericError')
    } else {
      lurker.viewMessageEvt('error', {
        accounts_reason: 'unexpected_error',
        accounts_error_message: (error && error.message) || 'unknown',
      })
      this.showError('showGenericError')
    }
    behaviors.scrollToTop()
  }

  closeModal = () => {
    this.setState({ showMailSentModal: false })
  }

  render() {
    const {
      showGenericError,
      showEmailInBlackListError,
      showEmailDomainInBlockListError,
      showInvalidTokenError,
      showMailSentModal,
      loading,
      isSubmitting,
      fields: { email },
    } = this.state
    const { onCancel } = this.props
    const emailHasError = !email.valid && !email.pristine

    if (loading) {
      return <LoadingBackdrop />
    }

    if (showInvalidTokenError) {
      return this.renderErrorView(onCancel)
    }

    return (
      <div className="flex flex-col items-center">
        <CenterWrapper>
          <MailIcon />
        </CenterWrapper>
        <TitleStart>
          <PageTitle>Alteração de e-mail</PageTitle>
        </TitleStart>
        <Description>
          <Subtitle>Cadastre o e-mail que você costuma usar.</Subtitle>
        </Description>
        {showGenericError && (
          <ErrorMsgStartView>
            <GenericErrorBox text="Não foi possível continuar com a alteração de e-mail. Por favor, tente novamente mais tarde." />
          </ErrorMsgStartView>
        )}
        {showEmailInBlackListError && (
          <ErrorMsgStartView>
            <GenericErrorBox text="Este e-mail não pode ser utilizado." />
          </ErrorMsgStartView>
        )}
        {showEmailDomainInBlockListError && (
          <ErrorMsgStartView>
            <GenericErrorBox text="Este domínio de e-mail não pode ser utilizado." />
          </ErrorMsgStartView>
        )}
        <PageForm method="POST" onSubmit={this.handleSubmit} noValidate>
          <MailCheckFormItemWrapper>
            <MailCheckInput
              label="Novo e-mail"
              caption="Enviaremos um e-mail de ativação."
              labelweight="600"
              onChange={event => this.onEmailChange(event)}
              onBlur={event => this.onEmailBlur(event)}
              error={email.msg}
              hasError={emailHasError}
              inputRef={x => {
                this.inputs.email = x
              }}
              disabled={isSubmitting}
              value={email.value}
            />
          </MailCheckFormItemWrapper>
          <LoadingButton
            className="w-full"
            size="medium"
            disabled={isSubmitting || !email.valid}
            isLoading={isSubmitting}
            onClick={this.handleSubmit}
          >
            Solicitar alteração
          </LoadingButton>
          <Button
            className="w-full mt-2"
            size="small"
            variant="link"
            onClick={this.changeMailCancel(onCancel)}
          >
            Cancelar
          </Button>
        </PageForm>
        <EmailChangeActivationSentModal
          onClose={this.closeModal}
          email={email.value}
          open={showMailSentModal}
        />
      </div>
    )
  }
}

EmailChangeSendMailTemplate.propTypes = {
  onEmailSent: PropTypes.func,
  onCancel: PropTypes.func,
  onForgotPassword: PropTypes.func,
  token: PropTypes.string,
}

export default EmailChangeSendMailTemplate
